import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, X, Check, Loader2, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL } from '@/utils/apiUrl';


export default function MobileLoginQR({ isOpen, onClose }) {
  const [qrToken, setQrToken] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [status, setStatus] = useState('generating'); // generating, ready, scanned, error
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      generateQRToken();
    }
    return () => {
      setQrToken(null);
      setQrUrl(null);
      setStatus('generating');
    };
  }, [isOpen]);

  const generateQRToken = async () => {
    try {
      setStatus('generating');
      const storedToken = localStorage.getItem('session_token');
      
      const response = await fetch(`${API_URL}/api/auth/mobile-qr`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {})
        }
      });
      
      if (!response.ok) throw new Error('QR oluşturulamadı');
      
      const data = await response.json();
      setQrToken(data.qr_token);
      
      // Create mobile login URL
      const baseUrl = window.location.origin;
      const loginUrl = `${baseUrl}/mobile-login?token=${data.qr_token}`;
      setQrUrl(loginUrl);
      setStatus('ready');
      
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="font-bold text-white text-lg">Mobil Giriş</h3>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="text-center">
            {status === 'generating' && (
              <div className="py-12">
                <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
                <p className="text-slate-400">QR Kod oluşturuluyor...</p>
              </div>
            )}

            {status === 'ready' && qrUrl && (
              <>
                <div className="bg-white p-4 rounded-xl inline-block mb-4">
                  <QRCodeSVG 
                    value={qrUrl} 
                    size={200}
                    level="H"
                    includeMargin={false}
                  />
                </div>
                
                <div className="space-y-3 text-left bg-slate-800/50 rounded-xl p-4 mb-4">
                  <p className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="bg-cyan-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                    Telefonunuzun kamerasını açın
                  </p>
                  <p className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="bg-cyan-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                    QR kodu tarayın
                  </p>
                  <p className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="bg-cyan-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                    Açılan linke tıklayın
                  </p>
                </div>

                <p className="text-xs text-slate-500">
                  QR kod 5 dakika geçerlidir
                </p>
              </>
            )}

            {status === 'scanned' && (
              <div className="py-12">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <p className="text-green-400 font-medium">Giriş Başarılı!</p>
                <p className="text-slate-400 text-sm mt-2">Telefonunuzda açıldı</p>
              </div>
            )}

            {status === 'error' && (
              <div className="py-12">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-400" />
                </div>
                <p className="text-red-400 font-medium">Hata Oluştu</p>
                <p className="text-slate-400 text-sm mt-2">{error}</p>
                <Button onClick={generateQRToken} className="mt-4 bg-cyan-600">
                  Tekrar Dene
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
